import * as XLSX from 'xlsx';
import { DeduplicationState } from '../types';

/**
 * Exports duplicate keywords analysis to Excel
 * Format: Keyword | View Group | Volume | Kept In | Strategy
 */
export const exportDuplicatesAnalysis = (
  deduplicationState: DeduplicationState,
  projectName: string
) => {
  // Build the data array with headers
  const data: (string | number)[][] = [
    ['Keyword', 'View Group', 'Volume', 'Kept In', 'Strategy'],
  ];

  // Get strategy label
  const strategyLabels = {
    'volume': 'Highest Volume',
    'intent-score': 'Highest Intent Score',
    'first': 'First Occurrence'
  };
  const strategyLabel = strategyLabels[deduplicationState.strategy];

  // Group instances by keyword + group for better organization
  const groupedInstances = new Map<string, typeof deduplicationState.duplicateInstances>();
  
  deduplicationState.duplicateInstances.forEach(instance => {
    const key = `${instance.keyword}|||${instance.groupName}`;
    if (!groupedInstances.has(key)) {
      groupedInstances.set(key, []);
    }
    groupedInstances.get(key)!.push(instance);
  });

  // Sort groups by keyword name
  const sortedGroups = Array.from(groupedInstances.entries()).sort((a, b) => 
    a[0].localeCompare(b[0])
  );

  // Add rows for each instance
  sortedGroups.forEach(([_, instances]) => {
    // Only include groups that have duplicates (more than 1 instance)
    if (instances.length > 1) {
      instances.forEach(instance => {
        data.push([
          instance.keyword,
          instance.groupName,
          instance.volume,
          instance.isKept ? instance.productName : '(removed)',
          strategyLabel
        ]);
      });
    }
  });

  // Create worksheet and workbook
  const worksheet = XLSX.utils.aoa_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Duplicate Keywords');

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  // Create blob and download
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${projectName.replace(/[^a-z0-9]/gi, '_')}_duplicate_keywords_analysis.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
